import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

function Hero() {
  const slides = [
    {
      image: 'images/landingbg1.webp',
      title: 'Shaping the Future of Education Research in West Africa',
      subtitle: 'Our Vision',
      description: 'Intellectual Oasis Fellowship is building a research hub that advances evidence-based policy and practice for students and schools.'
    },
    {
      image: 'images/landingbg2.webp',
      title: 'A Fellowship of Education Scholars',
      subtitle: 'Collaborative Network',
      description: 'Join a growing community producing data, insights, and peer‑reviewed contributions.'
    },
    {
      image: 'images/landingbg3.webp',
      title: 'Research, Publishing, and Open Data',
      subtitle: 'Impact at Scale',
      description: 'We generate policy papers and maintain a data‑rich repository to inform decision‑makers.'
    },
    {
      image: 'images/landingbg4.webp',
      title: 'Scholarships and EdTech for Access',
      subtitle: 'Equity & Access',
      description: 'Targeted scholarships and practical study tools that help students learn better.'
    },
    {
      image: 'images/landingbg5.webp',
      title: 'By 2035: A Continental Research Hub',
      subtitle: 'Long‑Term Goal',
      description: 'We aim to influence policy and improve systems across Nigeria, West Africa, and Africa.'
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAnimating, slides.length]);

  // Parallax effect based on scroll position (optimized with RAF)
  useEffect(() => {
    let ticking = false;
    let rafId;
    const onScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          const y = window.scrollY;
          const limited = Math.max(-8, Math.min(8, y * 0.03)); // Reduced intensity
          setParallaxY(limited);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const handleSlideClick = (index) => {
    if (!isAnimating) {
      setCurrentSlide(index);
    }
  };

  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth',
    });
  };

  return (
    <header className="relative h-screen overflow-hidden bg-black">
      {/* Background Slides */}
      <div className="absolute inset-0" aria-live="polite" aria-atomic="true">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            className="absolute inset-0"
          >
            <motion.div
              className="absolute inset-0 bg-center bg-cover"
              style={{ 
                backgroundImage: `url(${slides[currentSlide].image})`,
                willChange: 'transform',
                y: parallaxY,
                contain: 'layout style paint'
              }}
              role="presentation"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" role="presentation" aria-hidden="true" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-2 sm:px-4" aria-live="polite" aria-atomic="true">
        <div className="max-w-5xl mx-auto text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mb-8"
              style={{ willChange: 'opacity, transform' }}
            >
              <h3 className="text-lg md:text-xl text-[rgb(130,88,18)] font-semibold mb-3">
                {slides[currentSlide].subtitle}
              </h3>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 leading-tight">
                {slides[currentSlide].title}
              </h1>
              <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
                {slides[currentSlide].description}
              </p>
              {/* Mission statement (static across slides) */}
              <p className="mt-4 text-base md:text-lg text-gray-200 max-w-3xl mx-auto" aria-label="Mission statement">
                Intellectual Oasis Fellowship is a nonprofit fellowship of education scholars, advancing research, scholarships, and technology for better learning in West Africa.
              </p>
            </motion.div>
          </AnimatePresence>

          {/* CTA buttons with staggered animation */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { staggerChildren: 0.1, delayChildren: 0.3 } } }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-12"
            style={{ willChange: 'opacity, transform' }}
          >
            <motion.a
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              href="#fellowship"
              className="group relative px-6 py-3 bg-[rgb(130,88,18)] text-white rounded-full overflow-hidden transition-all duration-300 hover:bg-[rgb(110,68,0)] focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <span className="relative z-10 text-base font-medium">Join the Fellowship</span>
              <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </motion.a>

            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}>
              <Link
                to="/donate"
                className="group px-6 py-3 border-2 border-white text-white rounded-full text-base font-medium hover:bg-white hover:text-[rgb(130,88,18)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                Support Our Work
              </Link>
            </motion.div>

            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}>
              <Link
                to="/scholarships"
                className="group px-6 py-3 border-2 border-white/70 text-white rounded-full text-base font-medium hover:bg-white hover:text-[rgb(130,88,18)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                Apply for Scholarship
              </Link>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
            style={{ willChange: 'opacity, transform', contain: 'layout' }}
          >
            {[
              { number: '30+', label: 'Education Scholars' },
              { number: '50+', label: 'Research Projects' },
              { number: '35+', label: 'Published Papers' },
              { number: '300+', label: 'Students Supported' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-gray-300 text-xs md:text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Slide Navigation */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex space-x-3" role="tablist" aria-label="Slide navigation">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSlideClick(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : undefined}
              role="tab"
            />
          ))}
        </div>

        {/* Scroll Down Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.4 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
          onClick={handleScrollDown}
        >
          <div className="flex flex-col items-center text-white/80 hover:text-white transition-colors">
            <span className="text-[rgb(130,88,18)] font-medium mb-2">SCROLL DOWN</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, repeatType: 'loop' }}
              style={{ willChange: 'transform' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </header>
  );
}

export default Hero;